import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutDashboard, Wallet, FileText, Users, TrendingUp, Settings, ChevronDown, LogOut } from 'lucide-react';
import { useAuth } from '../../lib/auth';
import { cn } from '../../lib/utils';
import { CommandTrigger } from '../command/CommandBar';
import ThemeToggle from '../ui/ThemeToggle';

interface NavContext {
  id: string;
  label: string;
  icon: React.ElementType;
  routes: { path: string; label: string }[];
  matchPrefixes?: string[];
}

const contexts: NavContext[] = [
  {
    id: 'overview',
    label: 'Overview',
    icon: LayoutDashboard,
    routes: [{ path: '/', label: 'Dashboard' }],
  },
  {
    id: 'money',
    label: 'Money',
    icon: Wallet,
    routes: [
      { path: '/money', label: 'Money Hub' },
      { path: '/accounts', label: 'Accounts' },
    ],
    matchPrefixes: ['/money', '/accounts', '/debts', '/taxes', '/mileage', '/bills'],
  },
  {
    id: 'documents',
    label: 'Documents',
    icon: FileText,
    routes: [
      { path: '/invoicing/invoices', label: 'Invoices' },
      { path: '/invoicing/recurring', label: 'Recurring' },
      { path: '/invoicing/clients', label: 'Clients' },
      { path: '/documents/capture', label: 'Capture' },
      { path: '/documents/history', label: 'History' },
    ],
  },
  {
    id: 'people',
    label: 'People',
    icon: Users,
    routes: [
      { path: '/payroll/employees', label: 'Employees' },
      { path: '/payroll/timesheet', label: 'Timesheet' },
      { path: '/payroll/paychecks', label: 'Paychecks' },
      { path: '/payroll/upload-paystub', label: 'Upload Paystub' },
      { path: '/payroll/tax-summary', label: 'Payroll Taxes' },
      { path: '/contractors', label: 'Contractors' },
      { path: '/inventory/vendors', label: 'Vendors' },
    ],
  },
  {
    id: 'insights',
    label: 'Insights',
    icon: TrendingUp,
    routes: [
      { path: '/reports', label: 'Reports Hub' },
      { path: '/reports/profit-loss', label: 'Profit & Loss' },
      { path: '/reports/balance-sheet', label: 'Balance Sheet' },
      { path: '/reports/cash-flow', label: 'Cash Flow' },
      { path: '/accounting/chart-of-accounts', label: 'Chart of Accounts' },
      { path: '/accounting/journal', label: 'Journal' },
      { path: '/inventory/products', label: 'Inventory' },
    ],
  },
];

export default function ContextNav() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);

  const currentContext =
    contexts.find((ctx) => {
      const routeMatch = ctx.routes.some(
        (r) => location.pathname === r.path || location.pathname.startsWith(r.path + '/'),
      );
      const prefixMatch = ctx.matchPrefixes?.some(
        (prefix) => location.pathname === prefix || location.pathname.startsWith(prefix + '/'),
      );
      return routeMatch || prefixMatch;
    }) || contexts[0];

  return (
    <header className="fixed top-0 left-0 right-0 z-40">
      {/* Unified glass navbar — single seamless bar */}
      <div className="bg-white/70 dark:bg-slate-950/70 backdrop-blur-2xl border-b border-white/20 dark:border-slate-700/30 shadow-[0_1px_3px_rgba(0,0,0,0.05)] dark:shadow-[0_1px_3px_rgba(0,0,0,0.3)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            {/* Logo */}
            <NavLink to="/" className="flex items-center shrink-0">
              <img src="/capitro-logo.png" alt="Capitro" className="h-7" />
            </NavLink>

            {/* Context tabs — seamless pill switcher */}
            <nav className="flex items-center gap-0.5 mx-4">
              {contexts.map((ctx) => {
                const isActive = ctx.id === currentContext.id;
                const Icon = ctx.icon;

                return (
                  <NavLink
                    key={ctx.id}
                    to={ctx.routes[0].path}
                    className={cn(
                      'relative flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-[13px] font-medium transition-all duration-200',
                      isActive
                        ? 'text-white'
                        : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-100/60 dark:hover:bg-slate-800/40',
                    )}
                  >
                    {isActive && (
                      <motion.div
                        layoutId="activeCtx"
                        className="absolute inset-0 bg-gradient-to-r from-violet-600 to-violet-500 dark:from-violet-500 dark:to-violet-400 rounded-lg shadow-md shadow-violet-500/25"
                        transition={{ type: 'spring', bounce: 0.15, duration: 0.4 }}
                      />
                    )}
                    <span className="relative flex items-center gap-1.5">
                      <Icon className="w-3.5 h-3.5" />
                      <span className="hidden lg:inline">{ctx.label}</span>
                    </span>
                  </NavLink>
                );
              })}
            </nav>

            {/* Right side — unified, no visual breaks */}
            <div className="flex items-center gap-1.5">
              <CommandTrigger />
              <ThemeToggle />

              {/* User avatar */}
              <div className="relative ml-1">
                <button
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="flex items-center gap-1.5 p-1 rounded-lg hover:bg-slate-100/60 dark:hover:bg-slate-800/40 transition-colors"
                >
                  {user?.imageUrl ? (
                    <img
                      src={user.imageUrl}
                      alt={user.name || 'User'}
                      className="w-7 h-7 rounded-lg object-cover ring-1 ring-slate-200/50 dark:ring-slate-700/50"
                    />
                  ) : (
                    <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-[11px] font-bold text-white ring-1 ring-violet-400/30">
                      {(user?.displayName || user?.username)?.[0]?.toUpperCase() || 'U'}
                    </div>
                  )}
                  <ChevronDown className="w-3 h-3 text-slate-400" />
                </button>

                <AnimatePresence>
                  {showDropdown && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setShowDropdown(false)} />
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 4 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 4 }}
                        transition={{ duration: 0.15 }}
                        className="absolute right-0 top-full mt-1.5 w-52 z-50 bg-white/95 dark:bg-slate-900/95 backdrop-blur-2xl border border-slate-200/60 dark:border-slate-700/50 rounded-xl shadow-xl shadow-black/10 dark:shadow-black/30 overflow-hidden"
                      >
                        <div className="px-3 py-2.5 border-b border-slate-100 dark:border-slate-800/50">
                          <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                            {user?.displayName || user?.username}
                          </p>
                          <p className="text-[11px] text-slate-400 truncate">{user?.email}</p>
                        </div>
                        <div className="p-1">
                          <NavLink
                            to="/settings"
                            onClick={() => setShowDropdown(false)}
                            className="flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors"
                          >
                            <Settings className="w-3.5 h-3.5" />
                            Settings
                          </NavLink>
                          <button
                            onClick={() => {
                              logout();
                              setShowDropdown(false);
                            }}
                            className="flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm text-slate-500 dark:text-slate-400 hover:bg-red-50 dark:hover:bg-red-950/30 hover:text-red-600 dark:hover:text-red-400 transition-colors w-full"
                          >
                            <LogOut className="w-3.5 h-3.5" />
                            Sign out
                          </button>
                        </div>
                      </motion.div>
                    </>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sub-nav — flows as extension of main bar, no visual separation */}
      <AnimatePresence mode="wait">
        {currentContext.routes.length > 1 && (
          <motion.div
            key={currentContext.id}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            className="bg-white/50 dark:bg-slate-950/50 backdrop-blur-xl border-b border-slate-200/30 dark:border-slate-800/20"
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <nav className="flex items-center gap-0.5 py-2 overflow-x-auto scrollbar-hide">
                {currentContext.routes.map((route) => (
                  <NavLink
                    key={route.path}
                    to={route.path}
                    end={route.path === '/'}
                    className={({ isActive }) =>
                      cn(
                        'px-3 py-1.5 rounded-md text-[13px] font-medium whitespace-nowrap transition-all duration-150',
                        isActive
                          ? 'bg-violet-50 dark:bg-violet-500/10 text-violet-700 dark:text-violet-300'
                          : 'text-slate-500 dark:text-slate-500 hover:text-slate-800 dark:hover:text-slate-300 hover:bg-slate-100/50 dark:hover:bg-slate-800/30',
                      )
                    }
                  >
                    {route.label}
                  </NavLink>
                ))}
              </nav>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
